import { z } from "zod";
import { withHttps } from "./webAddress";

/** M12 round three (brief `docs/briefs/m12-public-api.md`, draft 2, choices 5 and 7): the webhooks' rules, one place for the settings page,
 *  the API, the worker and the docs (the prototype's `lib/sites.ts` rules moved here). A webhook is an https address of a site's that hears,
 *  signed, what happens in the workspace: the five events below (the test event `ping` is the settings page's button, never subscribed).
 *  A failed send is tried again on the schedule; a 410 switches the address off at once, three days of failures too. Pure. */

/** what a site can hear, in the brief's order, with the settings page's words */
export const WEBHOOK_EVENTS = ["ticket.created", "ticket.updated", "ticket.resolved", "message.created", "message.sent"] as const;
export type WebhookEvent = (typeof WEBHOOK_EVENTS)[number];
export const WEBHOOK_EVENT_LABELS: Record<WebhookEvent, string> = {
  "ticket.created": "A ticket is created", "ticket.updated": "A ticket's status, assignee or tags change", "ticket.resolved": "A ticket is resolved",
  "message.created": "A customer writes", "message.sent": "A reply leaves",
};
/** the type of a send: one of the five, or the test event */
export type WebhookSendEvent = WebhookEvent | "ping";
export const webhookEventLabel = (e: string): string => (e === "ping" ? "Test event" : WEBHOOK_EVENT_LABELS[e as WebhookEvent] ?? e);

/** minutes after each failed try (1 min, 5 min, 30 min, 2 h, 6 h, 12 h, 24 h): eight tries over about 45 hours */
export const WEBHOOK_RETRY_MINUTES = [1, 5, 30, 120, 360, 720, 1440] as const;
export const WEBHOOK_MAX_TRIES = WEBHOOK_RETRY_MINUTES.length + 1;
/** addresses a workspace may hold (choice 2) */
export const WEBHOOK_ENDPOINTS_PER_WORKSPACE = 5;
/** the log keeps a send this long (choice 7), then the nightly sweep deletes it */
export const WEBHOOK_SENDS_KEPT_DAYS = 30;
/** an address failing this long without one good answer is switched off (choice 7: "three days of failures") */
export const WEBHOOK_DISABLE_AFTER_HOURS = 72;
/** how old a signed message may be when the site checks it (the docs' replay window; each try is signed at its own time) */
export const WEBHOOK_REPLAY_WINDOW_SECONDS = 300;
/** the site's answer kept in the log: its first characters */
export const WEBHOOK_ANSWER_EXCERPT = 200;

/** when the next try is due after `attempts` tries, or null when none is left */
export function webhookNextTryAt(attempts: number, from: Date): Date | null {
  if (attempts >= WEBHOOK_MAX_TRIES || attempts < 1) return null;
  return new Date(from.getTime() + WEBHOOK_RETRY_MINUTES[attempts - 1]! * 60_000);
}

/* ---------- the routes' shapes ---------- */

const events = z.array(z.enum(WEBHOOK_EVENTS)).min(1).max(WEBHOOK_EVENTS.length).transform((e) => WEBHOOK_EVENTS.filter((x) => e.includes(x)));
const projects = z.array(z.string().min(1).max(100)).max(50);
/** a new address: where, which events (at least one of the five), for which projects (none: every project); the secret is ours, never sent */
export const WebhookEndpointCreateSchema = z.object({ url: z.string().trim().min(1).max(2000).transform(withHttps), events, projects: projects.default([]) }).strict();
export type WebhookEndpointCreate = z.infer<typeof WebhookEndpointCreateSchema>;
/** switching an address off and on, or changing what it hears */
export const WebhookEndpointPatchSchema = z.object({ enabled: z.boolean().optional(), events: events.optional(), projects: projects.optional() }).strict()
  .refine((p) => Object.keys(p).length > 0, "Name at least one change");
export type WebhookEndpointPatch = z.infer<typeof WebhookEndpointPatchSchema>;

export const WEBHOOK_STATES = ["active", "failing", "disabled"] as const;
/** an address as the list shows it: never its secret */
export const WebhookEndpointSchema = z.object({
  id: z.string(), url: z.string(), host: z.string(), siteId: z.string(), site: z.string(), events: z.array(z.enum(WEBHOOK_EVENTS)), projects: z.array(z.string()),
  state: z.enum(WEBHOOK_STATES), failures: z.number().int(), failingSince: z.string().nullable(), lastOkAt: z.string().nullable(),
  disabledAt: z.string().nullable(), /** "gone": the site answered 410; "failures": three days of failures; "hand": switched off on the page */ disabledWhy: z.enum(["gone", "failures", "hand"]).nullable(),
  createdAt: z.string(),
  /** M14 round three: the site's own address that Connect your site made (shown under Settings › Integrations › WordPress, not in the table) */
  fromConnect: z.boolean(),
});
export type WebhookEndpoint = z.infer<typeof WebhookEndpointSchema>;
/** the one answer that carries the secret */
export const WebhookEndpointCreatedSchema = z.object({ webhook: WebhookEndpointSchema, secret: z.string() });
export type WebhookEndpointCreated = z.infer<typeof WebhookEndpointCreatedSchema>;

export const WEBHOOK_SEND_STATES = ["queued", "retrying", "sent", "failed"] as const;
/** one send in the log: the event, the ticket it was about, the tries, the site's last answer (its code and first characters) */
export const WebhookSendSchema = z.object({
  id: z.string(), event: z.string(), ticketNumber: z.number().int().nullable(), state: z.enum(WEBHOOK_SEND_STATES), attempts: z.number().int(),
  nextAt: z.string().nullable(), status: z.number().int().nullable(), answer: z.string().nullable(), createdAt: z.string(), lastAt: z.string().nullable(),
});
export type WebhookSend = z.infer<typeof WebhookSendSchema>;
/** the log's answer in words: "200 OK", "410 Gone", "No answer" */
export const webhookAnswerLabel = (status: number | null): string => (status == null ? "No answer" : status === 200 ? "200 OK" : status === 410 ? "410 Gone" : String(status));
export const webhookOk = (status: number | null): boolean => status != null && status >= 200 && status < 300;

/** What a site receives (choice 7): the event's id (stable over every try), its number in the workspace's log (`seq`: no order is promised
 *  across retries, the site sorts by it), the type, when it happened, the workspace, the key that caused it (`origin`, so a site skips its
 *  own writes) and the ticket's snapshot with the message or the changes. Never a note, a draft or another customer. The post-as-member
 *  round: the delivery to the ticket's own site carries the member's address (`message.author.email`); nothing else does. */
export type WebhookPayload = {
  id: string; seq: number | null; type: WebhookSendEvent; createdAt: string; workspace: { slug: string; name: string }; origin: { keyId: string } | null;
  data: { ticket?: Record<string, unknown>; message?: Record<string, unknown>; changes?: Record<string, unknown> };
};

/* ---------- a reply on its way to the site (choice 5) ---------- */

/** a site reply's delivery as the thread shows it: handed over (waiting for the first answer), waiting after a failed try, delivered on
 *  the site's 2xx, mailed after the last try (a confirmed address) or not delivered (the banner) */
export type SiteDeliveryState = "handed" | "waiting" | "delivered" | "mailed" | "failed";
export type SiteDelivery = { host: string; state: SiteDeliveryState; tries: number; nextAt: string | null; mailedTo: string | null };
/** the line under the reply, in words */
export function siteDeliveryLine(d: SiteDelivery): string {
  switch (d.state) {
    case "handed": return `Goes to ${d.host}`;
    case "waiting": return `Waiting for ${d.host} · try ${d.tries} of ${WEBHOOK_MAX_TRIES} failed`;
    case "delivered": return `Delivered to ${d.host}`;
    case "mailed": return `Not delivered to ${d.host} · sent by mail to ${d.mailedTo ?? "the customer"}`;
    default: return `Not delivered to ${d.host}`;
  }
}
